import { calculateTaxMinor } from './priceFormatter';

const MAX_STRIPE_AMOUNT = 99_999_999;
const SPECIAL_TWO_DECIMAL_API_CURRENCIES = new Set(['ISK', 'UGX']);

function currencyCode(value) {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : null;
}

function checkedAdd(left, right) {
  const result = left + right;
  return Number.isSafeInteger(result) && result <= MAX_STRIPE_AMOUNT
    ? result
    : null;
}

function checkedMultiply(left, right) {
  const result = left * right;
  return Number.isSafeInteger(result) && result <= MAX_STRIPE_AMOUNT
    ? result
    : null;
}

export function exactCatalogMinorAmount(value, currency) {
  const code = currencyCode(currency);
  if (
    !code ||
    !Number.isSafeInteger(value) ||
    value < 0 ||
    value > MAX_STRIPE_AMOUNT ||
    (SPECIAL_TWO_DECIMAL_API_CURRENCIES.has(code) && value % 100 !== 0)
  ) return null;
  return value;
}

function validPaymentStore(store) {
  const currency = currencyCode(store?.currency);
  if (
    !store?.id ||
    store.paymentSchemaVersion !== 2 ||
    store.catalogPriceSchemaVersion !== 3 ||
    !currency ||
    !Number.isSafeInteger(store.taxRateBasisPoints) ||
    store.taxRateBasisPoints < 0 ||
    store.taxRateBasisPoints > 10_000
  ) return null;
  return { currency, taxRateBasisPoints: store.taxRateBasisPoints };
}

function exactCompatibleUnitAmount(priceInfo, currency) {
  if (!priceInfo || typeof priceInfo !== 'object') return null;
  const canonical = exactCatalogMinorAmount(priceInfo.unitAmount, currency);
  if (canonical == null) return null;
  if (Object.prototype.hasOwnProperty.call(priceInfo, 'price')) {
    const legacy = exactCatalogMinorAmount(priceInfo.price, currency);
    if (legacy == null || legacy !== canonical) return null;
  }
  return canonical;
}

function validPaymentProduct(product, store, currency) {
  if (
    !product ||
    product.priceSchemaVersion !== 3 ||
    product.storeId !== store.id ||
    currencyCode(product.priceInfo?.currency) !== currency
  ) return null;
  const baseMinor = exactCompatibleUnitAmount(product.priceInfo, currency);
  return baseMinor == null ? null : baseMinor;
}

export function calculateCartLineMinor(item, product, store) {
  const storePayment = validPaymentStore(store);
  if (
    !storePayment ||
    !Number.isSafeInteger(item?.quantity) ||
    item.quantity < 1 ||
    item.quantity > 1_000
  ) return null;
  const { currency } = storePayment;
  const baseMinor = validPaymentProduct(product, store, currency);
  if (baseMinor == null) return null;

  const selected = Array.isArray(item.selectedModifiers) ? item.selectedModifiers : [];
  const identities = new Set();
  let modifiersMinor = 0;
  for (const selection of selected) {
    const groupId = selection?.groupID;
    const optionId = selection?.optionID;
    if (typeof groupId !== 'string' || typeof optionId !== 'string') return null;
    const identity = `${groupId}\u0000${optionId}`;
    if (identities.has(identity)) return null;
    identities.add(identity);
    const group = product.modifierGroups?.find(candidate => candidate.id === groupId);
    const option = group?.modifierOptions?.find(candidate => candidate.id === optionId);
    if (
      !option ||
      currencyCode(option.priceInfo?.currency) !== currency
    ) return null;
    const optionMinor = exactCompatibleUnitAmount(option.priceInfo, currency);
    if (optionMinor == null) return null;
    modifiersMinor = checkedAdd(modifiersMinor, optionMinor);
    if (modifiersMinor == null) return null;
  }

  const unitMinor = checkedAdd(baseMinor, modifiersMinor);
  const lineTotalMinor = unitMinor == null
    ? null
    : checkedMultiply(unitMinor, item.quantity);
  if (lineTotalMinor == null) return null;
  return { currency, baseMinor, modifiersMinor, unitMinor, lineTotalMinor };
}

export function calculateCartEstimate(items, products, store) {
  const storePayment = validPaymentStore(store);
  if (!storePayment || !Array.isArray(items) || !Array.isArray(products)) {
    return { valid: false, subtotalMinor: null, taxMinor: null, totalMinor: null };
  }

  let subtotalMinor = 0;
  let taxableSubtotalMinor = 0;
  for (const item of items) {
    const product = products.find(candidate => candidate.id === item?.productID);
    const line = calculateCartLineMinor(item, product, store);
    if (!line) {
      return { valid: false, subtotalMinor: null, taxMinor: null, totalMinor: null };
    }
    subtotalMinor = checkedAdd(subtotalMinor, line.lineTotalMinor);
    if (subtotalMinor == null) {
      return { valid: false, subtotalMinor: null, taxMinor: null, totalMinor: null };
    }
    if (product.taxable !== false) {
      taxableSubtotalMinor = checkedAdd(taxableSubtotalMinor, line.lineTotalMinor);
      if (taxableSubtotalMinor == null) {
        return { valid: false, subtotalMinor: null, taxMinor: null, totalMinor: null };
      }
    }
  }

  const taxMinor = calculateTaxMinor(
    taxableSubtotalMinor,
    storePayment.taxRateBasisPoints,
    storePayment.currency,
  );
  const totalMinor = taxMinor == null ? null : checkedAdd(subtotalMinor, taxMinor);
  if (taxMinor == null || totalMinor == null) {
    return { valid: false, subtotalMinor: null, taxMinor: null, totalMinor: null };
  }
  return {
    valid: true,
    currency: storePayment.currency,
    subtotalMinor,
    taxableSubtotalMinor,
    taxMinor,
    totalMinor,
  };
}
